import React from 'react';

// DOM renderer for the shared resource-document prose modules in
// ../content/. Each document's words live there once, as plain data, and
// are rendered twice: here for the portal, and by ../pdf/PdfContent.jsx
// for the react-pdf export.
//
// Inline "runs": string | { b: text } | { i: text } | { code: text, mono? }
//   | { a: { href, text, external? } }
//
// Blocks:
//   { type: 'lede', text }
//   { type: 'heading', level: 1|2|3, text }     1 → <h2>, 2 → <h3>, 3 → <h4>
//   { type: 'p', runs }
//   { type: 'fine', runs, pullUp? }             small ink-60 print; pullUp tucks it under a list
//   { type: 'list', ordered?, items: [{ runs, sub?: [{ runs }] }] }
//   { type: 'callout', label, runs }
//   { type: 'exampleBox', text }                bordered serif example blurb
//   { type: 'script', label, text }             blockquote talk-track
//   { type: 'emailTemplate', subject, paras }
//   { type: 'signature', lines, caption }
//
// Any block (or list item / template) may carry PDF-side variants —
// pdfText, pdfRuns, pdfLevel, pdfLabel, pdfParas, pdfSubject, pdfBreak —
// plus the render-target switches dom: false / pdf: false. This renderer
// skips blocks marked dom: false and ignores every pdf* field; the PDF
// renderer does the reverse. Docs whose two renderings share a sentence
// store it once; deliberate divergences stay visible side by side in data.

const FINE_STYLE = { fontSize: 13, color: 'var(--mb-ink-60)' };

function renderRuns(runs) {
  return runs.map((run, i) => {
    if (typeof run === 'string') return run;
    if (run.b !== undefined) return <strong key={i}>{run.b}</strong>;
    if (run.i !== undefined) return <em key={i}>{run.i}</em>;
    if (run.code !== undefined) {
      return (
        <code key={i} style={run.mono ? { fontFamily: 'var(--font-numeral)' } : undefined}>
          {run.code}
        </code>
      );
    }
    if (run.a) {
      return run.a.external ? (
        <a key={i} href={run.a.href} target="_blank" rel="noopener noreferrer">
          {run.a.text}
        </a>
      ) : (
        <a key={i} href={run.a.href}>{run.a.text}</a>
      );
    }
    return null;
  });
}

function renderBlock(block, i) {
  switch (block.type) {
    case 'lede':
      return (
        <p
          key={i}
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 22,
            lineHeight: 1.4,
            margin: '0 0 32px',
          }}
        >
          {block.text}
        </p>
      );
    case 'heading': {
      const Tag = block.level === 1 ? 'h2' : block.level === 2 ? 'h3' : 'h4';
      return <Tag key={i}>{block.text}</Tag>;
    }
    case 'p':
      return <p key={i}>{renderRuns(block.runs)}</p>;
    case 'fine':
      return (
        <p key={i} style={block.pullUp ? { ...FINE_STYLE, marginTop: -8 } : FINE_STYLE}>
          {renderRuns(block.runs)}
        </p>
      );
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag key={i}>
          {block.items.map((item, j) => (
            <li key={j}>
              {renderRuns(item.runs)}
              {item.sub && (
                <ul>
                  {item.sub.map((s, k) => (
                    <li key={k}>{renderRuns(s.runs)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </Tag>
      );
    }
    case 'callout':
      return (
        <div key={i} className="resource-callout">
          <p className="resource-callout__label">{block.label}</p>
          <p style={{ margin: 0 }}>{renderRuns(block.runs)}</p>
        </div>
      );
    case 'exampleBox':
      return (
        <p
          key={i}
          style={{
            background: 'var(--mb-paper)',
            border: '2px solid var(--mb-ink)',
            padding: '14px 18px',
            fontFamily: 'var(--font-serif)',
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          {block.text}
        </p>
      );
    case 'script':
      return (
        <blockquote key={i} className="resource-callout" style={{ background: 'var(--mb-paper)' }}>
          <p className="resource-callout__label">{block.label}</p>
          <p style={{ margin: 0 }}>{block.text}</p>
        </blockquote>
      );
    case 'emailTemplate':
      return (
        <div key={i} className="resource-callout" style={{ background: 'var(--mb-paper)' }}>
          <p style={{ margin: 0 }}>
            Subject: <strong>{block.subject}</strong>
            {block.paras.map((para, j) => (
              <React.Fragment key={j}>
                <br />
                <br />
                {para}
              </React.Fragment>
            ))}
          </p>
        </div>
      );
    case 'signature':
      return (
        <p
          key={i}
          style={{ fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: 20, marginTop: 24 }}
        >
          {block.lines.map((line, j) => (
            <React.Fragment key={j}>
              {line}
              <br />
            </React.Fragment>
          ))}
          <em style={{ fontSize: 14, color: 'var(--mb-ink-60)' }}>{block.caption}</em>
        </p>
      );
    default:
      return null;
  }
}

export default function DocContent({ blocks }) {
  return <>{blocks.filter((b) => b.dom !== false).map(renderBlock)}</>;
}
